import React, { useState, useEffect, useRef } from 'react';

const Step5Review = ({
   setCurrentStep,
   handleSubmitStep4,
   isSubmitting,
   formData,
   handleInputChange,
   handleSubmitStep1
}) => {
   const [editMode, setEditMode] = useState(false);
   const [isEditSubmitting, setIsEditSubmitting] = useState(false);
   const [editError, setEditError] = useState(null);
   const [localFormData, setLocalFormData] = useState({ ...formData });
   const [isLoading, setIsLoading] = useState(true);
   const [dataLoaded, setDataLoaded] = useState(false);
   // Estados para el selector de países
   const [countries, setCountries] = useState([]);
   const [loadingCountries, setLoadingCountries] = useState(false);
   const [countryError, setCountryError] = useState(null);
   // Estados para el selector de estados
   const [states, setStates] = useState([]);
   const [loadingStates, setLoadingStates] = useState(false);
   const [stateError, setStateError] = useState(null);

   // Usar una ref para almacenar los datos originales que nunca cambian
   const originalDataRef = useRef(null);

   // Efecto para cargar la lista de países cuando se activa el modo de edición
   useEffect(() => {
      if (editMode) {
         const fetchCountries = async () => {
            setLoadingCountries(true);
            setCountryError(null);
            try {
               const jwt = localStorage.getItem('jwt');
               const response = await fetch('https://pt-api.digit-a.net/api/catalogue/countries', {
                  method: 'GET',
                  headers: {
                     'Authorization': `Bearer ${jwt}`,
                     'Content-Type': 'application/json'
                  }
               });

               if (!response.ok) {
                  throw new Error(`Failed to fetch countries: ${response.status}`);
               }

               const data = await response.json();

               if (Array.isArray(data)) {
                  // Convertir el array de strings a array de objetos
                  const formattedCountries = data.map(countryName => ({
                     code: countryName,
                     name: countryName
                  }));

                  // Ordenar los países para que Estados Unidos aparezca primero
                  const sortedCountries = formattedCountries.sort((a, b) => {
                     if (a.name === "United States") return -1;
                     if (b.name === "United States") return 1;
                     return a.name.localeCompare(b.name); // Ordenar el resto alfabéticamente
                  });

                  setCountries(sortedCountries);
               } else {
                  throw new Error('Invalid data format received');
               }
            } catch (error) {
               console.error('Error loading countries:', error);
               setCountryError(`Error loading countries: ${error.message}`);
            } finally {
               setLoadingCountries(false);
            }
         };

         fetchCountries();
      }
   }, [editMode]);

   // Efecto para cargar los datos del usuario
   useEffect(() => {
      // Si ya hemos cargado datos, no hacemos nada
      if (dataLoaded) return;

      const fetchSavedUserData = async () => {
         setIsLoading(true);
         try {
            const jwt = localStorage.getItem('jwt');
            const userEmail = localStorage.getItem('userEmail');
            let fetchedData = null;

            if (jwt && userEmail) {
               try {
                  const response = await fetch('https://pt-api.digit-a.net/api/users/get-user-by-email', {
                     method: 'POST',
                     headers: {
                        'Authorization': `Bearer ${jwt}`,
                        'Content-Type': 'application/json'
                     },
                     body: JSON.stringify({ email: userEmail })
                  });

                  if (response.ok) {
                     const userData = await response.json();
                     console.log('User data from API:', userData);

                     // La estructura correcta es userData.user.steps.yourDetails
                     if (userData &&
                        userData.user &&
                        userData.user.steps &&
                        userData.user.steps.yourDetails) {

                        const userDetails = userData.user.steps.yourDetails;
                        console.log('User details from yourDetails:', userDetails);

                        fetchedData = {
                           firstName: userDetails.firstName || '',
                           lastName: userDetails.lastName || '',
                           phoneNumber: userDetails.phoneNumber || '',
                           country: userDetails.country || '',
                           state: userDetails.state || '',
                           zipCode: userDetails.zipCode || '',
                           address: userDetails.address || ''
                        };

                        console.log('Fetched data from API:', fetchedData);
                     }
                  }
               } catch (err) {
                  console.error('Error fetching from API:', err);
               }
            }

            // Crear datos de muestra, pero solo usar si no hay datos reales
            const sampleData = {
               firstName: formData.firstName || 'John',
               lastName: formData.lastName || 'Smith',
               phoneNumber: formData.phoneNumber || '+1 555-123-4567',
               country: formData.country || 'United States',
               state: formData.state || 'California',
               zipCode: formData.zipCode || '90210',
               address: formData.address || '123 Main St, Los Angeles'
            };

            // Usar datos del API si están disponibles, de lo contrario usar los datos de ejemplo
            const finalData = fetchedData || sampleData;
            console.log('Final data to use:', finalData);

            // Guardar los datos originales en la ref para poder restaurarlos después
            originalDataRef.current = { ...finalData };

            // Actualizar estado local con los datos
            setLocalFormData(finalData);

            // Marcar que ya hemos cargado los datos
            setDataLoaded(true);
         } catch (error) {
            console.error('Error in data loading process:', error);
         } finally {
            setIsLoading(false);
         }
      };

      fetchSavedUserData();
   }, [
      // Incluir todas las dependencias o deshabilitar la regla de ESLint
      dataLoaded,
      formData.firstName,
      formData.lastName,
      formData.phoneNumber,
      formData.country,
      formData.state,
      formData.zipCode,
      formData.address
   ]);
   // Manejador de cambio de inputs
   const handleLocalInputChange = (e) => {
      const { name, value } = e.target;
      setLocalFormData(prev => ({
         ...prev,
         [name]: value
      }));
   };
   const handleEditClick = () => {
      setEditMode(true);
   };
   const handleCancelEdit = () => {
      // Restaurar los datos originales desde la ref en lugar de formData
      if (originalDataRef.current) {
         setLocalFormData({ ...originalDataRef.current });
      } else {
         // Fallback por si originalDataRef no se ha establecido por alguna razón
         setLocalFormData({ ...formData });
      }
      setEditMode(false);
      setEditError(null);
   };
   const handleSaveChanges = async () => {
      setIsEditSubmitting(true);
      setEditError(null);
      try {
         const jwt = localStorage.getItem('jwt');
         if (!jwt) {
            throw new Error('No authentication token found');
         }
         const response = await fetch('https://pt-api.digit-a.net/api/steps/step-1', {
            method: 'POST',
            headers: {
               'Content-Type': 'application/json',
               'Authorization': `Bearer ${jwt}`
            },
            body: JSON.stringify(localFormData)
         });
         if (!response.ok) {
            const errorData = await response.json();
            throw new Error(errorData.message || 'Failed to update user details');
         }
         // Update the parent component's formData
         Object.keys(localFormData).forEach(key => {
            handleInputChange({
               target: { name: key, value: localFormData[key] }
            });
         });
         // Actualizar también los datos originales para futuras cancelaciones
         originalDataRef.current = { ...localFormData };
         setEditMode(false);
      } catch (error) {
         console.error('Error updating user details:', error);
         setEditError(error.message);
      } finally {
         setIsEditSubmitting(false);
      }
   };

   // Nuevo efecto para cargar estados cuando el país es United States
   useEffect(() => {
      // Solo cargar estados si estamos en modo edición y el país es United States
      if (editMode &&
         (localFormData.country === 'United States' ||
          localFormData.country === 'USA' ||
          localFormData.country === 'US')) {

         const fetchStates = async () => {
            setLoadingStates(true);
            setStateError(null);
            try {
               const jwt = localStorage.getItem('jwt');
               // Enviamos el nombre completo del país
               const requestBody = {
                  country: 'United States'
               };

               const response = await fetch('https://pt-api.digit-a.net/api/catalogue/states-by-country', {
                  method: 'POST',
                  headers: {
                     'Authorization': `Bearer ${jwt}`,
                     'Content-Type': 'application/json'
                  },
                  body: JSON.stringify(requestBody)
               });

               if (!response.ok) {
                  throw new Error(`Failed to fetch states: ${response.status}`);
               }

               const data = await response.json();

               if (Array.isArray(data)) {
                  const formattedStates = data.map(stateName => ({
                     code: stateName,
                     name: stateName
                  }));
                  setStates(formattedStates);
               } else {
                  throw new Error('Invalid state data format');
               }
            } catch (error) {
               console.error('Error fetching states:', error);
               setStateError('Error loading states');
            } finally {
               setLoadingStates(false);
            }
         };

         fetchStates();
      }
   }, [editMode, localFormData.country]);

   return (
      <div className="modal_content_body" data-testid="step5-review">
         <h3 className="ppt_form_title mb-2 text-center">Step 4: Review & Verify</h3>
         {editMode ? (
            <div className="d-flex justify-content-end gap-3 pt-4">
               <small
                  onClick={handleCancelEdit}
                  className="text_under d-flex align-items-center gap-2 pointer mb-3"
                  disabled={isEditSubmitting}
               >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-x-circle" viewBox="0 0 16 16">
                     <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14m0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16" />
                     <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                  </svg>
                  <b>Cancel Edit</b>
               </small>
            </div>
         ) : (
            <div className="d-flex justify-content-end gap-3 pt-4">
               <small
                  onClick={handleEditClick}
                  className="text_under d-flex align-items-center gap-2 pointer mb-3"
               >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-pencil-square" viewBox="0 0 16 16">
                     <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                     <path fill-rule="evenodd" d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z" />
                  </svg>
                  <b>Edit Fields</b>
               </small>
            </div>
         )}
         {isLoading ? (
            <div className="text-center py-5">
               <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
               </div>
               <p className="mt-3">Loading your information...</p>
            </div>
         ) : (
            <>
               {/* Mostrar un mensaje si estamos usando datos de ejemplo */}
               {localFormData.firstName === 'John' && localFormData.lastName === 'Smith' && (
                  <div className="alert alert-warning mb-4">
                     <strong>Note:</strong> You're seeing example data below that you can edit before proceeding.
                  </div>
               )}
               {editError && (
                  <div className="alert alert-danger mb-4">
                     {editError}
                  </div>
               )}
               <div className="row ppt_s2_fields">
                  <div className="col-12 col-lg-6">
                     <div className="ppt_input_style mb-4">
                        <label>First Name <abbr>*</abbr></label>
                        <div>
                           <input
                              type="text"
                              className="form-control"
                              name="firstName"
                              value={localFormData.firstName || ''}
                              onChange={handleLocalInputChange}
                              disabled={!editMode}
                           />
                        </div>
                     </div>
                  </div>
                  <div className="col-12 col-lg-6">
                     <div className="ppt_input_style mb-4">
                        <label>Last Name <abbr>*</abbr></label>
                        <div>
                           <input
                              type="text"
                              className="form-control"
                              name="lastName"
                              value={localFormData.lastName || ''}
                              onChange={handleLocalInputChange}
                              disabled={!editMode}
                           />
                        </div>
                     </div>
                  </div>
                  <div className="col-12 col-lg-6">
                     <div className="ppt_input_style mb-4">
                        <label>Phone Number <abbr>*</abbr></label>
                        <div>
                           <input
                              type="tel"
                              className="form-control"
                              name="phoneNumber"
                              value={localFormData.phoneNumber || ''}
                              onChange={handleLocalInputChange}
                              disabled={!editMode}
                           />
                        </div>
                     </div>
                  </div>
                  <div className="col-12 col-lg-6">
                     <div className="ppt_input_style mb-4">
                        <label>Country <abbr>*</abbr></label>
                        <div>
                           {editMode ? (
                              <>
                                 <select
                                    className="form-select"
                                    name="country"
                                    value={localFormData.country || ''}
                                    onChange={handleLocalInputChange}
                                    disabled={loadingCountries}
                                 >
                                    <option value="">Select a country</option>
                                    {loadingCountries ? (
                                       <option value="" disabled>Loading countries...</option>
                                    ) : (
                                       countries.map((country, index) => (
                                          <option
                                             key={index}
                                             value={country.code}
                                          >
                                             {country.name}
                                          </option>
                                       ))
                                    )}
                                 </select>
                                 {countryError && (
                                    <small className="text-danger">{countryError}</small>
                                 )}
                              </>
                           ) : (
                              <input
                                 type="text"
                                 className="form-control"
                                 name="country"
                                 value={localFormData.country || ''}
                                 onChange={handleLocalInputChange}
                                 disabled={true}
                              />
                           )}
                        </div>
                     </div>
                  </div>
                  {/* Renderizado condicional del campo State */}
                  {(localFormData.country === 'United States' || localFormData.country === 'USA' || localFormData.country === 'US') && (
                     <div className="col-12 col-lg-6">
                        <div className="ppt_input_style mb-4">
                           <label>State <abbr>*</abbr></label>
                           <div>
                              {editMode ? (
                                 <>
                                    <select
                                       className="form-select"
                                       name="state"
                                       value={localFormData.state || ''}
                                       onChange={handleLocalInputChange}
                                       disabled={loadingStates}
                                    >
                                       <option value="">Select a state</option>
                                       {loadingStates ? (
                                          <option value="" disabled>Loading states...</option>
                                       ) : (
                                          states.map((state, index) => (
                                             <option
                                                key={index}
                                                value={state.code}
                                             >
                                                {state.name}
                                             </option>
                                          ))
                                       )}
                                    </select>
                                    {stateError && (
                                       <small className="text-danger">{stateError}</small>
                                    )}
                                 </>
                              ) : (
                                 <input
                                    type="text"
                                    className="form-control"
                                    name="state"
                                    value={localFormData.state || ''}
                                    onChange={handleLocalInputChange}
                                    disabled={true}
                                 />
                              )}
                           </div>
                        </div>
                     </div>
                  )}
                  <div className="col-12 col-lg-6">
                     <div className="ppt_input_style mb-4">
                        <label>ZIP Code <abbr>*</abbr></label>
                        <div>
                           <input
                              type="text"
                              className="form-control"
                              name="zipCode"
                              value={localFormData.zipCode || ''}
                              onChange={handleLocalInputChange}
                              disabled={!editMode}
                           />
                        </div>
                     </div>
                  </div>
                  <div className="col-12">
                     <div className="ppt_input_style mb-4">
                        <label>Address <abbr>*</abbr></label>
                        <div>
                           <input
                              type="text"
                              className="form-control"
                              name="address"
                              value={localFormData.address || ''}
                              onChange={handleLocalInputChange}
                              disabled={!editMode}
                           />
                        </div>
                     </div>
                  </div>
               </div>
               {editMode ? (
                  <div className="d-flex justify-content-end gap-3 pt-4 kyc_btns">
                     <button
                        onClick={handleSaveChanges}
                        className="ppt_btn_primary alt"
                        disabled={isEditSubmitting}
                     >
                        {isEditSubmitting ? 'Saving...' : 'Save Changes'}
                     </button>
                  </div>
               ) : (
                  <div className="d-flex justify-content-end gap-3 pt-4 kyc_btns">
                     <button
                        onClick={handleSubmitStep4}
                        className="ppt_btn_primary alt"
                        disabled={isSubmitting}
                     >
                        {isSubmitting ? 'Submitting...' : 'Next'}
                     </button>
                  </div>
               )}
            </>
         )}
      </div>
   );
};

export default Step5Review;